import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { FeatureFlag, Route } from '@mews-distributor/common';
import { BookingEngine, ConfigurationResult } from '@mews-distributor/data-access';
import { primitiveTypeUniq } from '@mews/fp';
import { createAction } from '@mews/framework';
import { fetchCurrentDateAndOccupancyProductPricesWithCheck } from '../../api/actions/productPricing';
import { fetchReservationsCurrencyData, fetchReservationsPrice } from '../../api/actions/reservationPrices';
import { DEFAULT_CURRENCY_CODE } from '../../defaultCurrency';
import { getSelectedEnterpriseDefaultRateCurrencyCode } from '../../selectors/views/selectedEnterprise/selectedEnterprise';
import { DistributorPromiseThunkAction, DistributorThunkAction } from '../../types';
import { getUserCurrencyCode } from '../../utils/getUserCurrencyCode';
import { CURRENCY_CHANGE_FAILED, CURRENCY_CHANGE_STARTED, CURRENCY_CHANGE_SUCCEEDED } from '../app/actionTypes';
import {
    getCurrencyCode,
    getCurrencyCodeFromQuery,
    getCurrentRoute,
    getIsFxMulticurrencyServiceEnabled,
} from '../app/selectors';
import { isMultiHotel } from '../configuration/selectors';
import { fetchExchangeRates } from '../entities/exchangeRates/actions';
import { hasExchangeRates } from '../entities/exchangeRates/selectors';
import { areReservationsWithPromotedServices } from '../entities/reservations/selectors';
import { fetchCurrentDatesAvailability } from '../entities/services/actions';
import { getCurrencyCodes, getValidCurrencyCode, hasCurrencyCode } from '../globalization/selectors';
import { fetchPromotedServices } from '../promotedServices/actions';
import { getPromotedServiceIds } from '../promotedServices/selectors';
import { getSelectedHotelId } from '../resourceOrder/selectors';
import { hasSelectedCityEnterprisesCurrencyCodeAvailabilities } from './currencyAvailabilitySelectors';
import { getField } from './processConfiguration';

/**
 * Returns currency code only when all the enterprises have the same currency code.
 *
 * For other scenarios, we can't reasonably decide which one is right.
 */
const getEnterpriseCurrencyCode = (configurationResult: ConfigurationResult) => {
    const currencyCodes = pipe(
        configurationResult.enterprises,
        array.map((enterprise) => enterprise.defaultCurrencyCode),
        primitiveTypeUniq
    );

    const firstCurrencyCode = currencyCodes[0];

    return currencyCodes.length === 1 ? firstCurrencyCode : null;
};

/** Sets configured currency code only when it makes sense. */
export const setConfiguredCurrencyCode =
    ({
        bookingEngineConfiguration,
        configurationResult,
    }: {
        bookingEngineConfiguration: BookingEngine;
        configurationResult: ConfigurationResult;
    }): DistributorThunkAction =>
    (dispatch, getState) => {
        const state = getState();

        const currencyCodeFromQuery = getCurrencyCodeFromQuery(state);
        const validCurrencyCodeFromQuery =
            currencyCodeFromQuery != null ? getValidCurrencyCode(state, { currencyCode: currencyCodeFromQuery }) : null;

        const configurationCurrencyCode = getField(bookingEngineConfiguration, 'currencyCode');
        const enterpriseCurrencyCode = getEnterpriseCurrencyCode(configurationResult);

        let currencyCode = validCurrencyCodeFromQuery ?? configurationCurrencyCode ?? enterpriseCurrencyCode;

        if (currencyCode == null || !hasCurrencyCode(getState(), { currencyCode })) {
            const multiHotel = isMultiHotel(getState());

            currencyCode = multiHotel
                ? DEFAULT_CURRENCY_CODE
                : getSelectedEnterpriseDefaultRateCurrencyCode(getState(), {});
        }

        void dispatch(setCurrency({ currencyCode, shouldFetchAvailabilityAndPricing: false }));
    };

export const setDetectedCurrency =
    (): DistributorPromiseThunkAction =>
    async (dispatch, getState, { featureFlagService }) => {
        const isUserCurrencyDetectionEnabled = featureFlagService.hasFlag(FeatureFlag.userCurrencyDetectionEnabled);
        const isFxMulticurrencyServiceEnabled = getIsFxMulticurrencyServiceEnabled(getState());

        if (!isUserCurrencyDetectionEnabled || !isFxMulticurrencyServiceEnabled) {
            return;
        }

        const state = getState();
        const currencyCodeFromQuery = getCurrencyCodeFromQuery(state);
        const validCurrencyCodeFromQuery =
            currencyCodeFromQuery != null ? getValidCurrencyCode(state, { currencyCode: currencyCodeFromQuery }) : null;

        //  The only scenario where we don't want to overwrite the set currency is when it's provided via URL parameter
        if (validCurrencyCodeFromQuery !== null) {
            return;
        }

        const supportedCurrencyCodes = getCurrencyCodes(getState());
        const userCurrencyCode = getUserCurrencyCode(supportedCurrencyCodes);

        if (userCurrencyCode !== null) {
            await dispatch(setCurrency({ currencyCode: userCurrencyCode }));
        }
    };

export const setCurrency =
    ({
        currencyCode,
        shouldFetchAvailabilityAndPricing = true,
    }: {
        currencyCode: string;
        shouldFetchAvailabilityAndPricing?: boolean;
    }): DistributorPromiseThunkAction =>
    async (dispatch, getState, { environmentService, loggerService, gainsightService }) => {
        const previousCurrencyCode = getCurrencyCode(getState());
        try {
            dispatch(createAction(CURRENCY_CHANGE_STARTED));
            const state = getState();
            const currentRoute = getCurrentRoute(state);
            if (currentRoute === Route.Rates) {
                const promotedServices = getPromotedServiceIds(state);
                if (array.isNonEmpty(promotedServices) && !areReservationsWithPromotedServices(state)) {
                    await dispatch(fetchPromotedServices({ currencyCode }));
                }
            }
            if (shouldFetchAvailabilityAndPricing) {
                await dispatch(fetchCurrentDatesAvailability({ currencyCode }));
                if (!hasSelectedCityEnterprisesCurrencyCodeAvailabilities(getState(), { currencyCode })) {
                    throw new Error('Currency has not been fetched.');
                }
            }

            const enterpriseId = getSelectedHotelId(state);
            const hasPropertyExchangeRates = enterpriseId && hasExchangeRates(state, { enterpriseId, currencyCode });
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (enterpriseId && !hasPropertyExchangeRates) {
                await dispatch(fetchExchangeRates({ enterpriseId, sourceCurrencyCode: currencyCode }));
            }

            await dispatch(fetchCurrentDateAndOccupancyProductPricesWithCheck({ currencyCode }));
            await dispatch(fetchReservationsCurrencyData({ currencyCode }));

            gainsightService.setGlobalContext({ currencyDisplayed: currencyCode });
            dispatch(createAction(CURRENCY_CHANGE_SUCCEEDED, { currencyCode }));
            if (currentRoute === Route.Summary || currentRoute === Route.Checkout) {
                await dispatch(fetchReservationsPrice());
            }
        } catch (error) {
            // @ts-expect-error environmentService not typed properly
            const locationHref = environmentService.get(['location', 'href']);

            loggerService.error(
                'distributor-global-setCurrency',
                `Currency has not been fetched, keeping previous currency.`,
                {
                    error,
                    currencyCode,
                    previousCurrencyCode,
                    locationHref,
                }
            );

            dispatch(createAction(CURRENCY_CHANGE_FAILED));
        }
    };
